import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useClientUiSettings } from '../../../hooks/useClientUiSettings';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';

// Icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VideocamIcon from '@mui/icons-material/Videocam';
import NotesIcon from '@mui/icons-material/Notes';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';

// Reasoning glyph icons — must match the icon set in ReasoningTimeline.jsx
// so therapists can mentally connect "glyph in chat" → "row in timeline".
import PsychologyIcon from '@mui/icons-material/Psychology';
import HubIcon from '@mui/icons-material/Hub';
import DescriptionIcon from '@mui/icons-material/Description';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReplayIcon from '@mui/icons-material/Replay';

import conversationUIStyles from '../styles/conversationUIStyles';
import { getVoiceForMessage } from '../../../utils/voiceUtils';
import api from '../../../services/api';
import TextToSpeech from '../../TextToSpeech';
import { fetchReasoningTimeline } from '../../../services/gen2ReasoningApi';

// Gen2 Reasoning Trace Card - lazy-loaded per message on expand.
// Fetches all traces for the conversation (cached) and matches to message by nearest createdAt.
// NOTE: We use conversationId + timestamp matching because ReasoningTrace records
// are created non-blocking before the AI message is saved, so messageId is not available.
const gen2TraceCache = {};

// Map of conversationId → notable timeline events (keyed by createdAt ms).
// Populated once per conversation on first render of MessageArea so the
// per-message glyphs can be gated server-truth (notable flag) rather than
// trying to derive notability client-side from raw trace fields.
const gen2NotableCache = {};

// Glyph appearance per event kind. Kept locally in MessageArea so the gating
// logic doesn't have to import the full timeline component. KIND values
// match the EVENT_KIND() function in ReasoningTimeline.jsx exactly.
const NOTABLE_GLYPH = {
  consultation:       { Icon: HubIcon,            color: '#7b1fa2', tip: 'Consulted a specialist module' },
  deep_thinking:      { Icon: PsychologyIcon,     color: '#1565c0', tip: 'Paused to reason through the turn' },
  async_pending:      { Icon: HourglassEmptyIcon, color: '#ed6c02', tip: 'Background reasoning in progress' },
  async_done:         { Icon: HubIcon,            color: '#7b1fa2', tip: 'Background reasoning completed' },
  formulation_update: { Icon: DescriptionIcon,    color: '#2e7d32', tip: 'Formulation updated' },
  fallback:           { Icon: ReplayIcon,         color: '#757575', tip: 'Fell back from deep reasoning' }
};

function classifyNotableEvent(event) {
  if (event.fellBackToGen1 || event.fellBackToFastMode) return 'fallback';
  if (event.mode === 'consultation') return 'consultation';
  if (event.mode === 'deep_thinking') return 'deep_thinking';
  if (event.mode === 'fast_with_async') {
    return event.asyncStatus === 'pending' ? 'async_pending' : 'async_done';
  }
  if (event.formulationUpdated) return 'formulation_update';
  return null;
}

const Gen2ReasoningCard = ({ conversationId, messageTimestamp, token, gen2ApiBase = 'therapist' }) => {
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTrace = async () => {
      try {
        // Cache traces per conversation to avoid repeated API calls
        if (!gen2TraceCache[conversationId]) {
          // Route through the viewer's own authorization base — supervisors
          // must hit /clinical-supervisor/gen2/... or they get a 403 (they
          // are not the assignedTherapist).
          const base = gen2ApiBase === 'clinical-supervisor' ? 'clinical-supervisor' : 'therapist';
          const response = await api.get(`/${base}/gen2/reasoning-traces/${conversationId}`, token);
          gen2TraceCache[conversationId] = response.data?.traces || [];
        }
        const traces = gen2TraceCache[conversationId];

        // Match trace to message by nearest createdAt timestamp
        if (traces.length > 0 && messageTimestamp) {
          const msgTime = new Date(messageTimestamp).getTime();
          let bestMatch = null;
          let bestDelta = Infinity;
          for (const t of traces) {
            const delta = Math.abs(new Date(t.createdAt).getTime() - msgTime);
            // Only match traces within 30 seconds of message creation
            if (delta < bestDelta && delta < 30000) {
              bestDelta = delta;
              bestMatch = t;
            }
          }
          setTrace(bestMatch);
        }
      } catch (err) {
        setError('Could not load reasoning trace');
        console.error('Gen2 trace fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTrace();
  }, [conversationId, messageTimestamp, token, gen2ApiBase]);

  if (loading) return <CircularProgress size={16} sx={{ ml: 1 }} />;
  if (error) return <Typography variant="caption" color="error">{error}</Typography>;
  if (!trace) return <Typography variant="caption" color="text.secondary">No Gen2 trace available</Typography>;

  const { processingMode, triggerReason, latencyMs, reasoningOutput, consultationData, formulationSnapshot } = trace;

  const modeColors = {
    fast: { bg: '#e0e0e0', color: '#424242' },
    deep_thinking: { bg: '#bbdefb', color: '#1565c0' },
    consultation: { bg: '#e1bee7', color: '#7b1fa2' }
  };
  const modeStyle = modeColors[processingMode] || modeColors.fast;

  return (
    <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip
          label={processingMode?.replace('_', ' ') || 'unknown'}
          size="small"
          sx={{ backgroundColor: modeStyle.bg, color: modeStyle.color, fontWeight: 600, fontSize: '11px', textTransform: 'capitalize' }}
        />
        {latencyMs != null && (
          <Typography variant="caption" color="text.secondary">
            Processed in {(latencyMs / 1000).toFixed(1)}s
          </Typography>
        )}
        {formulationSnapshot && (
          <Chip
            label={formulationSnapshot.wasUpdatedThisTurn ? `Formulation updated (v${formulationSnapshot.version})` : `Formulation v${formulationSnapshot.version}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '10px' }}
          />
        )}
      </Box>

      {triggerReason && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
          Trigger: {triggerReason}
        </Typography>
      )}

      {reasoningOutput?.clinicalAnalysis && (processingMode === 'deep_thinking' || processingMode === 'consultation') && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight={600}>Clinical Analysis</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', whiteSpace: 'pre-wrap', mt: 0.25 }}>
            {reasoningOutput.clinicalAnalysis}
          </Typography>
        </Box>
      )}

      {reasoningOutput?.responsePlan && (processingMode === 'deep_thinking' || processingMode === 'consultation') && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight={600}>Response Plan</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', whiteSpace: 'pre-wrap', mt: 0.25 }}>
            {reasoningOutput.responsePlan}
          </Typography>
        </Box>
      )}

      {consultationData && processingMode === 'consultation' && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight={600}>
            Consultation: {consultationData.moduleKey} {consultationData.wasApplied ? '(applied)' : '(not applied)'}
          </Typography>
          {consultationData.recommendations?.length > 0 && (
            <Box sx={{ ml: 1, mt: 0.5 }}>
              {consultationData.recommendations.map((rec, i) => (
                <Typography key={i} variant="caption" display="block" sx={{ fontSize: '11px' }}>
                  • [{rec.priority}] {rec.area}: {rec.suggestion}
                </Typography>
              ))}
            </Box>
          )}
          {consultationData.suggestedApproach && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              Approach: {consultationData.suggestedApproach}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const MessageArea = ({
  messages,
  userRole,
  currentMode,
  onAnnotateMessage,
  onCreateCareNote,
  onPlayAudio,
  messageContainerRef,
  userIsNearBottomRef,
  onScroll,
  platformSettings,
  therapistSettings,
  clientId,
  highlightedMessageId,
  token,
  conversationId,
  annotationState,
  formatTimestamp,
  styles = conversationUIStyles,
  responsive = {},
  gen2ApiBase = 'therapist'
}) => {
  // Resolved role label (Meeting 3). Falls back to 'AI Therapist' if hook
  // hasn't loaded yet. Therapist-side view uses the therapist's own resolved
  // settings; for a per-conversation override the client's settings should
  // be fetched separately (out of scope for this pass).
  const { roleLabel = 'AI Therapist' } = useClientUiSettings();
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  const [expandedAnnotations, setExpandedAnnotations] = useState({});
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [editAnnotationText, setEditAnnotationText] = useState('');
  const [editAnnotationTags, setEditAnnotationTags] = useState('');
  const [streamingSupported, setStreamingSupported] = useState(null);
  const [providerDetected, setProviderDetected] = useState(false);
  const [expandedGen2, setExpandedGen2] = useState({});
  // Map<closestMessageTimestampMs, event> of notable timeline events for this
  // conversation. Used to gate the per-message reasoning glyph so therapists
  // only see it on messages that actually have something to inspect.
  const [notableEvents, setNotableEvents] = useState([]);
  const localContainerRef = useRef(null);
  const containerRef = messageContainerRef || localContainerRef;

  // Fetch notable reasoning events once per conversation. We do this
  // eagerly (not lazily on click) precisely so the UI can know upfront
  // which messages are worth offering a reasoning view on.
  useEffect(() => {
    if (!conversationId || !token) return;
    let cancelled = false;
    const cached = gen2NotableCache[conversationId];
    if (cached) {
      setNotableEvents(cached);
      return;
    }
    fetchReasoningTimeline(conversationId, token, gen2ApiBase)
      .then((data) => {
        if (cancelled) return;
        const events = data?.events || [];
        gen2NotableCache[conversationId] = events;
        setNotableEvents(events);
      })
      .catch((err) => {
        const status = err?.response?.status;
        // 404 is expected for Gen 1 conversations (no reasoning routes/data).
        // Anything else (esp. 401/403 from an auth-base mismatch) is a real
        // problem that previously hid the reasoning UI silently — surface it.
        if (status === 404) {
          console.debug('[MessageArea] reasoning timeline preload skipped (Gen1/no data):', status);
        } else {
          console.error('[MessageArea] reasoning timeline preload failed:', status || err?.message, '(base:', gen2ApiBase + ')');
        }
      });
    return () => { cancelled = true; };
  }, [conversationId, token, gen2ApiBase]);

  // Resolve a notable event for a given AI message timestamp using a 30s
  // window (same heuristic the legacy lazy-fetch path used). Returns the
  // event object or null.
  const findNotableEventForMessage = useCallback((messageTimestamp) => {
    if (!messageTimestamp || !notableEvents?.length) return null;
    const ts = new Date(messageTimestamp).getTime();
    let best = null;
    let bestDelta = Infinity;
    for (const ev of notableEvents) {
      const evTs = new Date(ev.createdAt).getTime();
      const delta = Math.abs(evTs - ts);
      if (delta < 30000 && delta < bestDelta) {
        best = ev;
        bestDelta = delta;
      }
    }
    return best;
  }, [notableEvents]);

  // Handle scroll to track position
  const handleLocalScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      
      if (userIsNearBottomRef) {
        userIsNearBottomRef.current = isNearBottom;
      }
      
      setShowScrollButton(!isNearBottom && messages.length > 5);
    }
    
    if (onScroll) {
      onScroll();
    }
  }, [containerRef, userIsNearBottomRef, onScroll, messages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (userIsNearBottomRef?.current && containerRef.current) {
      setTimeout(() => {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }, 50);
    }
  }, [messages, containerRef, userIsNearBottomRef]);

  // Scroll to highlighted message
  useEffect(() => {
    if (highlightedMessageId) {
      const messageElement = document.querySelector(`[data-message-id="${highlightedMessageId}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedMessageId]);

  // Detect TTS provider for streaming support
  useEffect(() => {
    const detectProvider = async () => {
      if (!token) {
        setProviderDetected(true);
        return;
      }

      try {
        const response = await api.get('/tts/provider-info', token, {
          params: { interface: 'therapist_review' }
        });
        const provider = response?.data?.provider?.toLowerCase() || 'elevenlabs';
        const supportsStreaming = provider === 'elevenlabs';
        setStreamingSupported(supportsStreaming);
        console.log('MessageArea - TTS provider:', provider, 'Streaming supported:', supportsStreaming);
      } catch (error) {
        console.error('Failed to detect TTS provider:', error);
        setStreamingSupported(false);
      } finally {
        setProviderDetected(true);
      }
    };

    detectProvider();
  }, [token]);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleMessageMenuOpen = (event, message) => {
    setMessageMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMessageMenuClose = () => {
    setMessageMenuAnchor(null);
    setSelectedMessage(null);
  };

  const handleCopyMessage = () => {
    if (selectedMessage) {
      navigator.clipboard.writeText(selectedMessage.text);
      setCopySuccess(selectedMessage._id);
      setTimeout(() => setCopySuccess(null), 2000);
    }
    handleMessageMenuClose();
  };

  const handleAnnotate = () => {
    if (selectedMessage && onAnnotateMessage) {
      onAnnotateMessage(selectedMessage._id);
    }
    handleMessageMenuClose();
  };

  const handleCreateCareNote = () => {
    if (selectedMessage && onCreateCareNote) {
      onCreateCareNote(selectedMessage.text, selectedMessage._id);
    }
    handleMessageMenuClose();
  };


  const getModeIcon = (mode, sentInMode) => {
    const actualMode = sentInMode || mode;
    switch (actualMode) {
      case 'ai':
        return <SmartToyIcon sx={{ fontSize: 14 }} />;
      case 'human':
        return <PersonIcon sx={{ fontSize: 14 }} />;
      case 'assisted':
        return <RecordVoiceOverIcon sx={{ fontSize: 14 }} />;
      case 'live_call':
        return <VideocamIcon sx={{ fontSize: 14 }} />;
      default:
        return null;
    }
  };

  const getModeBadgeColor = (mode, sentInMode) => {
    const actualMode = sentInMode || mode;
    switch (actualMode) {
      case 'ai':
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          color: '#1565C0',
          border: '1px solid rgba(33, 150, 243, 0.3)'
        };
      case 'human':
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          color: '#2E7D32',
          border: '1px solid rgba(76, 175, 80, 0.3)'
        };
      case 'assisted':
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          color: '#EF6C00',
          border: '1px solid rgba(255, 140, 0, 0.3)'
        };
      case 'live_call':
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          color: '#6A1B9A',
          border: '1px solid rgba(156, 39, 176, 0.3)'
        };
      default:
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          color: '#757575'
        };
    }
  };

  const renderMessage = (message, index) => {
    const isClient = message.sender === 'client' || message.senderRole === 'client';
    const isSystem = message.sender === 'system' || message.senderRole === 'system';
    const isAI = message.sender === 'ai' || 
                 message.senderRole === 'ai_therapist' || 
                 message.senderRole === 'ai' ||
                 (message.sentInMode === 'ai' && !isClient && !isSystem);
    const isTherapist = !isClient && !isSystem && !isAI && (
                        message.sender === 'therapist' || 
                        message.senderRole === 'therapist' || 
                        message.senderRole === 'human_therapist');
    const isAnnotating = annotationState?.annotatingMessageId === message._id;
    const isHighlighted = highlightedMessageId === message._id;

    // Handle system messages separately - no bubble styling
    if (isSystem) {
      return (
        <Box
          key={message._id || index}
          data-message-id={message._id}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: 1.5,
            px: 3
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: '#94a3b8',
            fontSize: '13px'
          }}>
            <Box sx={{ 
              flex: '1 1 0', 
              height: '1px', 
              background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1))' 
            }} />
            <Typography variant="caption" sx={{ 
              fontWeight: 600,
              color: '#94a3b8',
              textTransform: 'uppercase',
              fontSize: '11px',
              letterSpacing: '0.5px'
            }}>
              System
            </Typography>
            <Typography variant="caption" sx={{ 
              color: '#64748b',
              fontStyle: 'italic'
            }}>
              {message.text}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: '#cbd5e1',
              fontSize: '11px'
            }}>
              {formatTimestamp ? formatTimestamp(message.timestamp) : 
               new Date(message.timestamp).toLocaleTimeString([], { 
                 hour: '2-digit', 
                 minute: '2-digit' 
               })}
            </Typography>
            <Box sx={{ 
              flex: '1 1 0', 
              height: '1px', 
              background: 'linear-gradient(90deg, rgba(0,0,0,0.1), transparent)' 
            }} />
          </Box>
        </Box>
      );
    }

    let messageStyle = styles.messageBubble;
    if (isClient) {
      messageStyle = { ...messageStyle, ...styles.clientMessage };
    } else if (isAI) {
      // AI messages use blue gradient, right-aligned
      messageStyle = { ...messageStyle, ...styles.aiTherapistMessage };
    } else if (isTherapist) {
      // Human therapist messages use orange gradient, right-aligned
      messageStyle = { ...messageStyle, ...styles.therapistMessage };
    }

    return (
      <Box
        key={message._id || index}
        data-message-id={message._id}
        sx={{
          ...messageStyle,
          ...(isHighlighted && {
            animation: 'pulse 2s ease-in-out',
            boxShadow: '0 0 20px rgba(255,140,0,0.3)'
          })
        }}
      >
        {/* Message Header */}
        <Box sx={{
          ...styles.messageHeader,
          color: isClient ? '#6A1B9A' : (isAI ? '#1565C0' : '#EF6C00')
        }}>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {isClient ? 'Client' : (isAI ? roleLabel : 'Therapist')}
          </Typography>

          {/* Voice input indicator for client messages */}
          {isClient && message.metadata?.inputMode === 'voice' && (
            <Tooltip title="Sent via Advanced Voice Mode">
              <HeadsetMicIcon sx={{ fontSize: '14px', color: '#7c3aed', ml: 0.5 }} />
            </Tooltip>
          )}
          {isClient && message.inputMethod === 'voice' && !message.metadata?.inputMode && (
            <Tooltip title="Sent via voice input">
              <MicIcon sx={{ fontSize: '14px', color: '#94a3b8', ml: 0.5 }} />
            </Tooltip>
          )}

          {(isTherapist || isAI) && message.sentInMode && (
            <Chip
              icon={getModeIcon(currentMode, message.sentInMode)}
              label={message.sentInMode}
              size="small"
              sx={{
                ...styles.modeIndicator,
                ...getModeBadgeColor(currentMode, message.sentInMode)
              }}
            />
          )}
          
          {/* Show if message was edited from AI suggestion */}
          {message.sentInMode === 'assisted' && message.aiSuggestionHandling === 'SENT_EDITED' && (
            <Tooltip 
              title="This message was edited from an AI suggestion" 
              placement="top"
            >
              <Typography variant="caption" component="span" sx={{ 
                fontStyle: 'italic',
                opacity: 0.5,
                fontSize: '10px',
                ml: 0.5,
                cursor: 'help',
                textTransform: 'lowercase'
              }}>
                • edited
              </Typography>
            </Tooltip>
          )}
          
          {/* Show if message was written manually in assisted mode */}
          {message.sentInMode === 'assisted' && message.aiSuggestionHandling !== 'SENT_AS_IS' && message.aiSuggestionHandling !== 'SENT_EDITED' && (
            <Tooltip 
              title={
                message.aiSuggestionHandling === 'REJECTED_MANUAL' 
                  ? "This message was written manually after rejecting an AI suggestion"
                  : "This message was written manually without AI assistance"
              }
              placement="top"
            >
              <Typography variant="caption" component="span" sx={{ 
                fontStyle: 'italic',
                opacity: 0.5,
                fontSize: '10px',
                ml: 0.5,
                cursor: 'help',
                textTransform: 'lowercase'
              }}>
                • manual
              </Typography>
            </Tooltip>
          )}
          
          <Typography variant="caption" sx={{ 
            ml: 'auto',
            opacity: 0.7
          }}>
            {formatTimestamp ? formatTimestamp(message.timestamp) : 
             new Date(message.timestamp).toLocaleTimeString([], { 
               hour: '2-digit', 
               minute: '2-digit' 
             })}
          </Typography>
          
          {/* Audio Play/Stop Button with Progressive Playback */}
          {(isTherapist || isAI || message.senderRole === 'client') && (
            <TextToSpeech
              text={message.text}
              size="small"
              disabled={!providerDetected}
              useStreaming={streamingSupported === true}
              platformSettings={platformSettings}
              therapistSettings={therapistSettings}
              clientId={clientId}
              token={token}
              useBackendTTS={true}
              senderRole={message.senderRole}
              ttsInterface="therapist_review"
            />
          )}
          
          {userRole === 'therapist' && (
            <IconButton
              size="small"
              onClick={(e) => handleMessageMenuOpen(e, message)}
              sx={{ ml: 1, opacity: 0.7, '&:hover': { opacity: 1 } }}
              aria-label="Message options"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Message Text */}
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            // Text color is now handled by the message styles
          }}
        >
          {message.text}
        </Typography>

        {/* Gen2 Reasoning Trace - expandable, AI messages only */}
        {/* Reasoning glyph — only renders when this AI message has a notable
            Gen 2 trace (server-decided). Click to inspect inline; the same
            data also appears in the Reasoning Timeline side panel. */}
        {isAI && token && (() => {
          const event = findNotableEventForMessage(message.timestamp || message.createdAt);
          if (!event) return null;
          const kind = classifyNotableEvent(event);
          if (!kind) return null;
          const { Icon, color, tip } = NOTABLE_GLYPH[kind];
          return (
            <Box sx={{ mt: 0.5 }}>
              <Tooltip
                placement="top"
                title={
                  <>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>{tip}</Box>
                    {event.narrative && <Box sx={{ fontSize: 12 }}>{event.narrative}</Box>}
                  </>
                }
              >
                <IconButton
                  size="small"
                  onClick={() => setExpandedGen2(prev => ({ ...prev, [message._id]: !prev[message._id] }))}
                  sx={{
                    backgroundColor: color, color: 'white',
                    width: 24, height: 24, mr: 0.5,
                    '&:hover': { backgroundColor: color, opacity: 0.85 }
                  }}
                >
                  <Icon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Typography
                component="span"
                variant="caption"
                onClick={() => setExpandedGen2(prev => ({ ...prev, [message._id]: !prev[message._id] }))}
                sx={{ cursor: 'pointer', color: 'text.secondary', fontSize: 11, userSelect: 'none', verticalAlign: 'middle', '&:hover': { textDecoration: 'underline' } }}
              >
                {expandedGen2[message._id] ? 'Hide reasoning' : 'View reasoning'}
              </Typography>
              <Collapse in={expandedGen2[message._id]}>
                {expandedGen2[message._id] && (
                  <Gen2ReasoningCard conversationId={conversationId} messageTimestamp={message.timestamp || message.createdAt} token={token} gen2ApiBase={gen2ApiBase} />
                )}
              </Collapse>
            </Box>
          );
        })()}

        {/* Copy Success Indicator */}
        {copySuccess === message._id && (
          <Typography variant="caption" sx={{ color: '#4CAF50', mt: 1 }}>
            Copied to clipboard!
          </Typography>
        )}

        {/* Annotations - Collapsible with indicator */}
        {message.annotations && message.annotations.length > 0 && (
          <Box sx={{ 
            mt: 1.5,
            backgroundColor: 'rgba(156, 39, 176, 0.04)',
            borderRadius: '8px',
            border: '1px solid rgba(156, 39, 176, 0.15)',
            overflow: 'hidden'
          }}>
            <Box 
              onClick={() => setExpandedAnnotations(prev => ({ 
                ...prev, 
                [message._id]: !prev[message._id] 
              }))}
              sx={{ 
                p: 1,
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                cursor: 'pointer',
                backgroundColor: 'rgba(156, 39, 176, 0.06)',
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.08)'
                }
              }}
            >
              <CommentIcon sx={{ fontSize: 16, color: '#7B1FA2' }} />
              <Typography variant="caption" sx={{ 
                fontWeight: 500, 
                color: '#7B1FA2',
                flexGrow: 1
              }}>
                Therapist Notes ({message.annotations.length})
              </Typography>
              {expandedAnnotations[message._id] ? 
                <ExpandLessIcon sx={{ fontSize: 18, color: '#7B1FA2' }} /> : 
                <ExpandMoreIcon sx={{ fontSize: 18, color: '#7B1FA2' }} />
              }
            </Box>
            
            <Collapse in={expandedAnnotations[message._id]}>
              <Box sx={{ p: 1.5, pt: 0 }}>
                {message.annotations.map((annotation, idx) => (
                  <Box key={annotation._id || idx} sx={{ 
                    mt: 1,
                    p: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '4px',
                    position: 'relative'
                  }}>
                    {editingAnnotation === annotation._id ? (
                      // Edit mode
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={editAnnotationText}
                          onChange={(e) => setEditAnnotationText(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Tags (comma-separated)"
                          value={editAnnotationTags}
                          onChange={(e) => setEditAnnotationTags(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={async () => {
                              if (annotationState?.handleEditAnnotation) {
                                await annotationState.handleEditAnnotation(
                                  message._id,
                                  annotation._id,
                                  {
                                    text: editAnnotationText,
                                    tags: editAnnotationTags.split(',').map(t => t.trim()).filter(t => t)
                                  }
                                );
                              }
                              setEditingAnnotation(null);
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setEditingAnnotation(null)}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      // View mode
                      <>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 4, 
                          right: 4,
                          display: 'flex',
                          gap: 0.5
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingAnnotation(annotation._id);
                              setEditAnnotationText(annotation.text);
                              setEditAnnotationTags(Array.isArray(annotation.tags) ?
                                annotation.tags.join(', ') : annotation.tags || '');
                            }}
                            sx={{
                              padding: '2px',
                              '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' }
                            }}
                            aria-label="Edit annotation"
                          >
                            <EditIcon sx={{ fontSize: 16, color: '#7B1FA2' }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (annotationState?.handleDeleteAnnotation &&
                                  window.confirm('Delete this annotation?')) {
                                annotationState.handleDeleteAnnotation(message._id, annotation._id);
                              }
                            }}
                            sx={{
                              padding: '2px',
                              '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
                            }}
                            aria-label="Delete annotation"
                          >
                            <DeleteIcon sx={{ fontSize: 16, color: '#F44336' }} />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="body2" sx={{ 
                          color: '#4A148C',
                          fontSize: '13px',
                          lineHeight: 1.5,
                          pr: 6
                        }}>
                          {annotation.text}
                        </Typography>
                        
                        {annotation.tags && annotation.tags.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {(Array.isArray(annotation.tags) ? annotation.tags : annotation.tags.split(',')).map((tag, tagIdx) => (
                              <Chip
                                key={tagIdx}
                                label={typeof tag === 'string' ? tag.trim() : tag}
                                size="small"
                                sx={{ 
                                  height: 18, 
                                  fontSize: '10px',
                                  backgroundColor: '#E1BEE7',
                                  color: '#4A148C',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        
                        {annotation.timestamp && (
                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            mt: 0.5,
                            color: '#64748b',
                            fontSize: '11px'
                          }}>
                            Added {new Date(annotation.timestamp).toLocaleString()}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Annotation Input */}
        {isAnnotating && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Add annotation..."
              value={annotationState.annotationInput}
              onChange={(e) => annotationState.setAnnotationInput(e.target.value)}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="Tags (comma-separated)"
              value={annotationState.annotationTags}
              onChange={(e) => annotationState.setAnnotationTags(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => annotationState.handleSaveAnnotation(message._id)}
                disabled={!annotationState.annotationInput.trim()}
              >
                Save
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={annotationState.handleCancelAnnotation}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={styles.messageArea}>
      <Box
        ref={containerRef}
        onScroll={handleLocalScroll}
        sx={styles.messageContainer}
      >
        {messages.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            color: '#94a3b8'
          }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No messages yet
            </Typography>
            <Typography variant="body2">
              Start the conversation or wait for the client to send a message
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => renderMessage(message, index))
        )}
      </Box>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Tooltip title="Scroll to bottom">
          <IconButton
            onClick={scrollToBottom}
            sx={styles.scrollToBottom}
            aria-label="Scroll to bottom"
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Message Context Menu */}
      <Menu
        anchorEl={messageMenuAnchor}
        open={Boolean(messageMenuAnchor)}
        onClose={handleMessageMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: 180
          }
        }}
      >
        <MenuItem onClick={handleCopyMessage}>
          <ContentCopyIcon sx={{ mr: 1, fontSize: 18 }} />
          Copy Message
        </MenuItem>
        <MenuItem onClick={handleAnnotate}>
          <EditNoteIcon sx={{ mr: 1, fontSize: 18 }} />
          Add Annotation
        </MenuItem>
        <MenuItem onClick={handleCreateCareNote}>
          <NotesIcon sx={{ mr: 1, fontSize: 18 }} />
          Create Care Note
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MessageArea;